import React, { useState, useEffect } from 'react';


/******************************************************************************
 * [PREVIEW MODE ONLY] - MOCK API, STYLES, AND UTILITIES
 * ----------------------------------------------------------------------------
 * The code in this section is specifically added to make the component work
 * in this standalone preview environment. In your actual project, you would
 * replace these with your real API functions, CSS files, and icon libraries.
 * * LIVE INTEGRATION STEPS:
 * 1. Comment out or delete the `ApiConfig`, `JOB_ROLES_MOCK`, and `PostCallWithErrorResponse` blocks.
 * 2. Replace calls to `PostCallWithErrorResponse` with your actual `fetch` or `axios` calls.
 * 3. Extract the `LYWO_CSS` string into a standard `.css` file and import it at the top of this file (e.g., `import './QuizBuilder.css';`).
 * 4. Remove the `useEffect` inside `AiGenerator` that dynamically injects the style tag.
 * 5. Replace the `Icons` object with your project's icon library (like lucide-react, react-icons, or MUI icons) if preferred.
 ******************************************************************************/


/**
 * MODULE 1: CONFIGURATION & UTILITIES
 * -----------------------------------------------------------------------------
 */


const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 15);
};


// LIVE INSTRUCTION: Replace these API endpoints with your actual backend URLs
const ApiConfig = {
  REPLACE_QUEST: 'REPLACE_QUEST',
  GET_SUB_TOPICS: 'GET_SUB_TOPICS',
  GET_QUIZ_QUES: 'GET_QUIZ_QUES',
  CREATE_UPDATE_QUIZ: 'CREATE_UPDATE_QUIZ',
  EXTRACT_TOPICS: 'EXTRACT_TOPICS'
};


// LIVE INSTRUCTION: Fetch this list from your backend or keep it as a static config file
const JOB_ROLES_MOCK = [
  { id: 1, title: 'Senior Frontend Developer (React)', topics: ['Frontend', 'React', 'JavaScript', 'CSS', 'Web Performance'] },
  { id: 2, title: 'Backend Engineer (Node.js)', topics: ['Backend', 'Node.js', 'API Design', 'Database', 'Security'] },
  { id: 3, title: 'DevOps Engineer', topics: ['DevOps', 'AWS', 'Docker', 'Kubernetes', 'CI/CD'] },
  { id: 4, title: 'UI/UX Designer', topics: ['Design', 'Figma', 'Prototyping', 'User Research', 'Accessibility'] },
  { id: 5, title: 'Full Stack Developer', topics: ['React', 'Node.js', 'TypeScript', 'MongoDB', 'Express'] },
  { id: 6, title: 'Data Engineer', topics: ['Python', 'SQL', 'ETL', 'Spark', 'Data Warehousing'] },
  { id: 7, title: 'QA Automation Engineer', topics: ['Testing', 'Selenium', 'Cypress', 'Jest', 'CI/CD'] },
  { id: 8, title: 'Product Manager', topics: ['Agile', 'Scrum', 'Roadmap', 'User Stories', 'Jira'] },
  { id: 9, title: 'Mobile App Developer', topics: ['Flutter', 'React Native', 'iOS', 'Android', 'Dart'] },
  { id: 10, title: 'Cloud Architect', topics: ['AWS', 'Azure', 'System Design', 'Security', 'Networking'] }
];


// LIVE INSTRUCTION: Comment this mock function out entirely. Replace its usages with your real API handler.
const PostCallWithErrorResponse = async (url, payload) => {
  console.log(`[Mock API] POST ${url}`, payload);
  return new Promise(resolve => {
    setTimeout(() => {
      if (url === ApiConfig.EXTRACT_TOPICS) {
        const mockTopics = ["React Component Lifecycles", "State Management", "API Integration", "UI/UX Principles"];
        resolve({ json: { topics: mockTopics } });
      } else if (url === ApiConfig.GET_SUB_TOPICS) {
        const mockResponse = (payload.topics || []).map(t => ({
          topic: t,
          subtopics: [
            `Fundamentals of ${t}`,
            `${t} Best Practices`,
            `Advanced ${t} Concepts`,
            `${t} Performance Optimization`,
            `${t} Tools & Ecosystem`,
            `Security in ${t}`,
            `Testing ${t} Applications`,
            `Deployment Strategies for ${t}`
          ]
        }));
        resolve({ json: { sub_topics: mockResponse } });
      } else if (url === ApiConfig.GET_QUIZ_QUES) {
        const count = payload.num_questions || 3;
        const newQs = Array(parseInt(count)).fill(0).map((_, i) => ({
          q: `AI Generated Question ${i + 1} about ${payload.topics?.[0] || 'Topic'}`,
          options: ["Option A", "Option B", "Option C", "Option D"],
          correct_answer: "Option A",
          rationale: "AI Explanation for why Option A is correct. This concept is fundamental to understanding the core topic.",
          weight: 1,
          type: "MCQ"
        }));
        resolve({ json: { quiz: { quiz_questions: newQs } } });
      } else {
        resolve({ json: { success: true } });
      }
    }, 800);
  });
};


// LIVE INSTRUCTION: You can keep these inline SVGs or swap them out for an icon library like 'lucide-react'
const Icons = {
  Edit: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  Trash: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
  Play: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>,
  Chart: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>,
  Sparkles: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L12 3Z" /><path d="M5 3v4" /><path d="M9 5H3" /><path d="M19 19v2" /><path d="M21 20h-2" /></svg>,
  Grid: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>,
  List: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>,
  Check: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>,
  More: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></svg>,
  Close: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Copy: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>,
  Replace: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>,
  User: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
  Plus: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  Regenerate: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /></svg>,
  ArrowRight: (p) => <svg {...p} width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
};


/* ==============================================================================================
   1. DESIGN SYSTEM & ASSETS (Merged)
   ============================================================================================== */


// LIVE INSTRUCTION: Move all CSS from this string into a standard CSS file (e.g., AiWizard.css)
// and import it using `import './AiWizard.css';`. Then delete this string constant.
const LYWO_CSS = `
@charset "UTF-8";
@import url("https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css");
@import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");


:root {
  --brand-primary: #0d6efd;
  --brand-bg: #f5f8ff;
  --brand-text: #212529;
  --color-info-bg: #eef2ff;
  --color-success-bg: #d1e7dd; --color-success-text: #198754;
  --color-warning-bg: #fff3cd; --color-warning-text: #856404;
  --color-danger-bg: #f8d7da; --color-danger-text: #dc3545;
  --ui-gray-bg: #F2F4F7;
  --ui-border: #d0d5dd;
  --ui-text-muted: #667085;
  --ui-text-dark: #344054;
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
  --font-base: 11px;
}


html { zoom: 0.95; scroll-behavior: smooth; }


body {
  margin: 0; padding: 0;
  font-family: 'Inter', sans-serif !important;
  font-size: var(--font-base);
  background-color: var(--bs-body-bg);
  -webkit-font-smoothing: antialiased;
  line-height: 1.5;
}


.text-sm { font-size: 8px !important; line-height: 1.5; }
.text-base { font-size: 11px !important; line-height: 1.5; }
.fw-bold { font-weight: 700 !important; }
.fw-normal { font-weight: 400 !important; }
.fw-medium { font-weight: 500 !important; }


.ds-wrapper { background-color: var(--brand-bg); min-height: 100vh; display: flex; flex-direction: column; }
.ds-header { background: #fff; border-bottom: 1px solid var(--ui-border); position: sticky; top: 0; z-index: 1000; }
.ds-brand-row { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--ui-gray-bg); }
.ds-nav-row { padding: 0 1.5rem; overflow-x: auto; white-space: nowrap; background: #fff; }
.ds-content { flex: 1; padding: 2rem; width: 100%; max-width: 960px; margin: 0 auto; }


.nav-tabs-horizontal { display: flex; gap: 2rem; border-bottom: none; margin: 0; padding: 0; list-style: none; }
.nav-item-h { padding: 1rem 0.25rem; cursor: pointer; color: var(--ui-text-muted); font-weight: 500; font-size: 14px; border-bottom: 2px solid transparent; transition: all 0.2s; display: flex; align-items: center; gap: 0.5rem; }
.nav-item-h:hover, .nav-item-h.active { color: var(--brand-primary); }
.nav-item-h.active { border-bottom-color: var(--brand-primary); }


.btn { font-weight: 600; padding: 0.5rem 1rem; border-radius: var(--radius-md); display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; transition: 0.2s; font-size: 13px; cursor: pointer; border: 1px solid transparent; }
.btn-primary { background-color: var(--brand-primary); border-color: var(--brand-primary); color: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); }
.btn-primary:hover { background-color: #0b5ed7; transform: translateY(-1px); }
.btn-primary:disabled { background-color: #93c5fd; border-color: #93c5fd; cursor: not-allowed; transform: none; }
.btn-outline-primary { color: var(--brand-primary); border-color: var(--brand-primary); background: #fff; }
.btn-outline-primary:hover { background-color: var(--color-info-bg); }
.btn-icon { padding: 8px; color: var(--ui-text-muted); border: 1px solid var(--ui-border); background: #fff; border-radius: 4px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.btn-icon:hover { color: var(--brand-primary); border-color: var(--brand-primary); background: var(--color-info-bg); }


.form-control { color: #344054; font-size: 12px; font-weight: 500; border-radius: 8px; min-height: 38px; border: 1px solid var(--ui-border); padding: 0.375rem 0.75rem; background: #fff; width: 100%; }
.form-control:focus { outline: none; border-color: var(--brand-primary); box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.15); }
.form-label { font-size: 11px; font-weight: 500; color: #344054; margin-bottom: 4px; display: block; }
.redBorder { border-color: #dc3545 !important; }


.topic-chip { display: inline-flex; align-items: center; padding: 6px 14px; border-radius: 50rem; font-size: 11px; font-weight: 500; cursor: pointer; border: 1px solid var(--ui-border); background-color: #fff; color: var(--brand-text); transition: all 0.2s ease; }
.topic-chip:hover { background-color: var(--ui-gray-bg); }
.topic-chip.selected { background-color: var(--brand-primary); border-color: var(--brand-primary); color: #fff; }
.topic-chip.sub { font-size: 10px; padding: 4px 10px; border-color: #e5e7eb; }
.topic-chip.sub.selected { background-color: var(--color-info-bg); color: var(--brand-primary); border-color: var(--brand-primary); }
.input-with-action-icon { position: absolute; top: 50%; right: 12px; transform: translateY(-50%); color: var(--ui-text-muted); cursor: pointer; }
.input-with-action-icon:hover { color: var(--brand-primary); }


.context-menu { background: #fff; border: 1px solid var(--ui-border); border-radius: 8px; box-shadow: 0 4px 12px -2px rgba(0,0,0,0.1); min-width: 180px; padding: 4px 0; overflow: hidden; }
.context-menu-item { display: flex; align-items: center; gap: 8px; padding: 8px 12px; width: 100%; border: none; background: transparent; text-align: left; font-size: 14px; color: var(--brand-text); cursor: pointer; transition: background 0.2s; }
.context-menu-item:hover { background-color: #f8f9fa; color: var(--brand-primary); }
.context-menu-item.danger { color: var(--color-danger-text); }
.context-menu-item.danger:hover { background-color: #fef2f2; }


.badge { display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 9999px; font-size: 0.75rem; font-weight: 600; line-height: 1; border: 1px solid transparent; }
.badge-good { background-color: var(--color-info-bg); color: var(--brand-primary); border-color: var(--color-info-bg); }
.badge-normal { background-color: var(--color-warning-bg); color: var(--color-warning-text); border-color: var(--color-warning-bg); }


.card-std { background: #fff; border: 1px solid var(--ui-border); border-radius: 12px; padding: 12px; transition: 0.3s; position: relative; margin-bottom: 0.75rem; font-size: 11px; }
.information-card { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 4px 12px; border-radius: 6px; background-color: var(--color-warning-bg); color: var(--color-warning-text); border: 1px solid var(--color-warning-text); font-size: 8px; font-weight: 600; line-height: 1.25; }


.table-container { border: 1px solid var(--ui-border); border-radius: 8px; overflow: hidden; background: #fff; }
.ant-table { width: 100%; border-collapse: collapse; }
.ant-table th { background: #fafafa; padding: 10px 12px; text-align: left; font-weight: 700; font-size: 11px; color: var(--brand-text); border-bottom: 1px solid var(--ui-border); }
.ant-table td { padding: 10px 12px; font-size: 11px; color: #344054; border-bottom: 1px solid var(--ui-border); }
.ant-table tr:last-child td { border-bottom: none; }
.ant-table tr:hover td { background-color: #fafafa; }


/* MODAL STYLES */
.custom-modal-overlay { position: fixed; inset: 0; background-color: rgba(0,0,0,0.5); z-index: 1050; display: flex; align-items: center; justify-content: center; animation: fadeIn 0.2s ease-out; }
.custom-modal-dialog { background: #fff; width: 100%; max-height: 90vh; border-radius: var(--radius-lg); box-shadow: 0 10px 25px rgba(0,0,0,0.1); display: flex; flex-direction: column; overflow: hidden; animation: slideUp 0.3s ease-out; }
.custom-modal-header { padding: 0.5rem 1.5rem; border-bottom: 1px solid var(--ui-border); display: flex; justify-content: space-between; align-items: center; background: #fff; }
.custom-modal-body { padding: 1rem; flex: 1;  overflow-y: auto; background: #fff; }
.custom-modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--ui-border); background: #f8f9fa; display: flex; justify-content: flex-end; gap: 1rem; }
.anim-fade { animation: fadeIn 0.3s ease-out; }
.sticky-action-bar { padding: 1rem 1.5rem; border-top: 1px solid var(--ui-border); background: #fff; flex-shrink: 0; display: flex; justify-content: space-between; align-items: center; z-index: 10; }


/* REUSABLE MODAL LAYOUT CLASSES */
.lywo-modal-content { display: flex; flex-direction: column; height: 550px; }
.lywo-modal-body { flex: 1; overflow-y: auto; padding-right: 8px; display: flex; flex-direction: column; gap: 1.25rem; }
.lywo-modal-footer { display: flex; justify-content: space-between; padding-top: 0.75rem; border-top: 1px solid var(--ui-border); margin-top: auto; flex-shrink: 0; }


.hide-scrollbar::-webkit-scrollbar { display: none; }
.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }


@keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
`;


/* ==============================================================================================
   3. SHARED COMPONENTS
   ============================================================================================== */


const Modal = ({ title, onClose, children, footer, size = '600px' }) => (
  <div className="custom-modal-overlay">
    <div className="custom-modal-dialog" style={{ maxWidth: size }}>
      <div className="custom-modal-header">
        {typeof title === 'string' ? <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{title}</h3> : title}
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }} title="Close"><Icons.Close /></button>
      </div>
      <div className="custom-modal-body">{children}</div>
      {footer && <div className="custom-modal-footer">{footer}</div>}
    </div>
  </div>
);




/******************************************************************************
 * [CORE LIVE CODE] - AI GENERATOR COMPONENTS
 * ----------------------------------------------------------------------------
 * This section contains the actual React components (AiWizardModal and AiGenerator)
 * that you would integrate into your live application.
 * Ensure your real API functions replace the mocked versions here.
 ******************************************************************************/


/* ==============================================================================================
   4. AI GENERATOR COMPONENT
   ============================================================================================== */
const AiWizardModal = ({ onClose, onImport, initialTopic }) => {
  const [activeTab, setActiveTab] = useState('topics');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedSubTopics, setSelectedSubTopics] = useState([]);
  const [customTopic, setCustomTopic] = useState('');
  const [customSubTopic, setCustomSubTopic] = useState('');

  // LIVE INSTRUCTION: 'topics' state might need to be populated by an API on mount instead of hardcoded
  const [topics, setTopics] = useState(['Frontend', 'Backend', 'DevOps', 'Mobile', 'Design', 'React', 'Node.js', 'AWS']);

  const [count, setCount] = useState(5);
  const [difficulty, setDifficulty] = useState('Medium');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subTopics, setSubTopics] = useState([]);
  const [customSubInput, setCustomSubInput] = useState({});
  const [jobDescription, setJobDescription] = useState('');
  const [selectedJobRole, setSelectedJobRole] = useState(null);


  const canAccessSubtopics = selectedTopics.length > 0;
  const canAccessConfig = selectedSubTopics.length > 0;
  const hasQuestions = questions.length > 0;


  const getSubSubTopics = (topic) => {
    // LIVE INSTRUCTION: Swap PostCallWithErrorResponse with your real HTTP client (e.g. axios.post('/api/subtopics', ...))
    PostCallWithErrorResponse(ApiConfig.GET_SUB_TOPICS, {
      job_description: jobDescription,
      topics: selectedTopics
    }).then((res) => {
      if (res?.json?.sub_topics) {
        setSubTopics(res.json.sub_topics);
      }
    });
  }


  useEffect(() => {
    if (selectedTopics.length === 0) {
      setSubTopics([]);
      if (activeTab !== 'topics') setActiveTab('topics');
      return;
    }
    const timer = setTimeout(() => {
      getSubSubTopics();
    }, 1000);
    return () => clearTimeout(timer);
  }, [selectedTopics]);


  const handleAddCustom = () => {
    if (customTopic.trim()) {
      setTopics(prev => [...prev, customTopic]);
      setCustomTopic('');
    }
  };


  const handleCustomSubInput = (topic, val) => {
    setCustomSubInput({ ...customSubInput, [topic]: val });
  }


  const addCustomSubTopic = (topicName) => {
    const val = customSubInput[topicName];
    if (!val || !val.trim()) return;
    const newSubTopics = [...subTopics];
    const groupIdx = newSubTopics.findIndex(g => g.topic === topicName);
    if (groupIdx > -1) {
      if (!newSubTopics[groupIdx].subtopics.includes(val)) {
        newSubTopics[groupIdx].subtopics.push(val);
        setSelectedSubTopics(prev => [...prev, val]);
      }
    }
    setSubTopics(newSubTopics);
    setCustomSubInput({ ...customSubInput, [topicName]: '' });
  }


  const handleSelectJobRole = (role) => {
    setSelectedJobRole(role.id);
    setJobDescription(role.title);

    // Add any new topics from the role to the main selection pool
    setTopics(prev => [...new Set([...prev, ...role.topics])]);

    // Auto-select the topics from this role (up to max 5)
    setSelectedTopics(role.topics.slice(0, 5));
  };


  const generate = () => {
    if (!canAccessConfig) return alert("Please complete previous steps.");
    setLoading(true);


    // LIVE INSTRUCTION: Swap PostCallWithErrorResponse with your real HTTP client (e.g. axios.post('/api/questions/generate', ...))
    PostCallWithErrorResponse(ApiConfig.GET_QUIZ_QUES, {
      difficulty_level: difficulty,
      job_description: jobDescription,
      num_questions: count,
      topics: selectedSubTopics
    }).then((res) => {
      if (res?.json?.quiz?.quiz_questions) {
        setQuestions(res?.json?.quiz?.quiz_questions);
        setActiveTab('review');
      }
      setLoading(false);
    });
  };


  const remove = (idx) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };


  const handleImport = () => {
    const questionsMap = questions?.map((ques) => {
      return {
        ...ques,
        id: generateId(), // LIVE INSTRUCTION: Ensure ID generation mechanism matches backend schema
        weight: 1,
        type: "MCQ",
        text: ques.q
      }
    });
    onImport(questionsMap);
  };


  const renderTabs = (
    <div style={{ display: 'flex', gap: '24px', alignItems: 'center', height: '100%' }}>
      <button className={`nav-item-h ${activeTab === 'topics' ? 'active' : ''}`} onClick={() => setActiveTab('topics')} style={{ background: 'none', border: 'none', borderBottom: activeTab === 'topics' ? '2px solid var(--brand-primary)' : 'none', padding: '8px 0', fontSize: '11px', fontWeight: '600', color: activeTab === 'topics' ? 'var(--brand-primary)' : 'var(--ui-text-muted)', marginRight: '0' }}>Topics</button>
      <button className={`nav-item-h ${activeTab === 'subtopics' ? 'active' : ''}`} onClick={() => canAccessSubtopics && setActiveTab('subtopics')} disabled={!canAccessSubtopics} style={{ background: 'none', border: 'none', borderBottom: activeTab === 'subtopics' ? '2px solid var(--brand-primary)' : 'none', padding: '8px 0', fontSize: '11px', fontWeight: '600', color: activeTab === 'subtopics' ? 'var(--brand-primary)' : 'var(--ui-text-muted)', marginRight: '0', opacity: !canAccessSubtopics ? 0.5 : 1 }}>Sub Topics</button>
      <button className={`nav-item-h ${activeTab === 'configuration' ? 'active' : ''}`} onClick={() => canAccessConfig && setActiveTab('configuration')} disabled={!canAccessConfig} style={{ background: 'none', border: 'none', borderBottom: activeTab === 'configuration' ? '2px solid var(--brand-primary)' : 'none', padding: '8px 0', fontSize: '11px', fontWeight: '600', color: activeTab === 'configuration' ? 'var(--brand-primary)' : 'var(--ui-text-muted)', marginRight: '0', opacity: !canAccessConfig ? 0.5 : 1 }}>Configuration</button>
      {hasQuestions && (<button className={`nav-item-h ${activeTab === 'review' ? 'active' : ''}`} onClick={() => setActiveTab('review')} style={{ background: 'none', border: 'none', borderBottom: activeTab === 'review' ? '2px solid var(--brand-primary)' : 'none', padding: '8px 0', fontSize: '11px', fontWeight: '600', color: activeTab === 'review' ? 'var(--brand-primary)' : 'var(--ui-text-muted)', marginRight: '0' }}>Review Questions</button>)}
    </div>
  );


  return (
    <Modal title={renderTabs} onClose={onClose} size="1000px" footer={null}>
      <div className="lywo-modal-content">
        {activeTab === 'topics' && (
          <div className="anim-fade" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div className="lywo-modal-body">
              <div style={{ marginBottom: '0.5rem' }}>
                <h6 className="text-base fw-bold" style={{ fontSize: '13px', marginBottom: '12px' }}>Select A Job Role To Extract Topics &nbsp;(Max 5)</h6>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', maxHeight: '260px', overflowY: 'auto', paddingRight: '4px' }}>
                  {/* LIVE INSTRUCTION: Ensure JOB_ROLES_MOCK is swapped with fetched/real roles data */}
                  {JOB_ROLES_MOCK.map((role) => (
                    <div
                      key={role.id}
                      onClick={() => handleSelectJobRole(role)}
                      style={{
                        border: `1px solid ${selectedJobRole === role.id ? 'var(--brand-primary)' : 'var(--ui-border)'}`,
                        borderRadius: '8px',
                        padding: '10px 14px',
                        cursor: 'pointer',
                        backgroundColor: selectedJobRole === role.id ? 'var(--color-info-bg)' : '#fff',
                        transition: 'all 0.2s',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        overflow: 'hidden'
                      }}
                    >
                      <div style={{ fontWeight: '600', fontSize: '13px', color: selectedJobRole === role.id ? 'var(--brand-primary)' : 'var(--ui-text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {role.title}
                      </div>
                      <div className="hide-scrollbar" style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px' }}>
                        {role.topics.map(t => (
                          <span
                            key={t}
                            style={{
                              fontSize: '10px',
                              padding: '4px 8px',
                              backgroundColor: selectedJobRole === role.id ? '#fff' : '#f3f4f6',
                              border: selectedJobRole === role.id ? '1px solid var(--brand-primary)' : '1px solid transparent',
                              borderRadius: '4px',
                              color: selectedJobRole === role.id ? 'var(--brand-primary)' : '#4b5563',
                              fontWeight: '500',
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>


              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                <h6 className="text-base fw-bold" style={{ fontSize: '13px', margin: 0 }}>Add custom topic as needed</h6>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input className="form-control" placeholder="Add custom topic..." value={customTopic} onChange={(e) => setCustomTopic(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddCustom()} style={{ paddingRight: '35px', backgroundColor: '#f8f9fa' }} />
                  <div className="input-with-action-icon" style={{ fontSize: '11px' }} onClick={handleAddCustom}><Icons.Plus /></div>
                </div>
                <div className="hide-scrollbar" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '4px', maxHeight: '85px', overflowY: 'auto', alignContent: 'flex-start' }}>
                  {topics.map((job) => (<button key={job} onClick={() => { if (selectedTopics.includes(job)) setSelectedTopics((p) => p.filter((t) => t !== job)); else if (selectedTopics.length < 5) setSelectedTopics((p) => [...p, job]); }} className={`topic-chip ${selectedTopics.includes(job) ? 'selected' : ''}`}>{job}</button>))}
                </div>
              </div>
            </div>
            <div className="lywo-modal-footer">
              <div style={{ flexGrow: 1 }}></div>
              <button className="btn btn-primary" disabled={!canAccessSubtopics} onClick={() => setActiveTab('subtopics')}>Next: Sub Topics</button>
            </div>
          </div>
        )}
        {activeTab === 'subtopics' && (
          <div className="anim-fade" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ marginBottom: '1rem', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><h4 className="text-base fw-bold" style={{ marginBottom: '0.25rem', margin: 0 }}>SubTopics Extracted from Topics:</h4><span className="text-base" style={{ color: 'var(--ui-text-muted)' }}>Note: You can select a maximum of 12 subtopics.</span></div>
              <div className="text-base fw-bold">Sub Topics Selected: {selectedSubTopics.length}/12</div>
            </div>
            <div className="lywo-modal-body">
              {subTopics.length > 0 ? (
                <div>
                  {subTopics.map((group, groupIdx) => (
                    <div key={groupIdx} style={{ marginBottom: '1.5rem' }}>
                      <h6 className="text-base fw-bold mb-2" style={{ color: 'var(--brand-text)' }}>{group.topic}</h6>
                      <div style={{ position: 'relative', marginBottom: '1rem' }}>
                        <input className="form-control" placeholder="Add Custom Subtopics" value={customSubInput[group.topic] || ''} onChange={(e) => handleCustomSubInput(group.topic, e.target.value)} style={{ paddingRight: '35px', fontSize: '11px' }} onKeyDown={(e) => e.key === 'Enter' && addCustomSubTopic(group.topic)} />
                        <div className="input-with-action-icon" style={{ fontSize: '11px' }} onClick={() => addCustomSubTopic(group.topic)}><Icons.Plus /></div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        {group.subtopics.map((sub, idx) => (
                          <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', cursor: 'pointer', color: '#4b5563' }}><input type="checkbox" checked={selectedSubTopics.includes(sub)} onChange={() => { if (selectedSubTopics.includes(sub)) setSelectedSubTopics((p) => p.filter((t) => t !== sub)); else if (selectedSubTopics.length < 12) setSelectedSubTopics((p) => [...p, sub]); }} style={{ accentColor: 'var(--brand-primary)' }} />{sub}</label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (<div style={{ padding: '2rem', textAlign: 'center', color: '#666', background: '#f8f9fa', borderRadius: '8px', marginBottom: '1rem' }}><div className="spinner-border spinner-border-sm text-primary mb-2" role="status"></div><div>Fetching relevant sub-topics based on your selection...</div></div>)}
            </div>
            <div className="lywo-modal-footer">
              <button style={{ height: '32px', fontSize: '13px' }} className="btn btn-outline-primary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" style={{ height: '32px' }} disabled={!canAccessConfig} onClick={() => setActiveTab('configuration')}>Next: Configuration</button>
            </div>
          </div>
        )}
        {activeTab === 'configuration' && (
          <div className="anim-fade" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div className="lywo-modal-body">
              <h6 className="text-base fw-bold" style={{ fontSize: '13px', marginBottom: '1rem' }}>Quiz Parameters</h6>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <div className="spinner-border text-primary mb-2" role="status" style={{ width: '2rem', height: '2rem' }}></div><p className='text-base fw-normal' style={{ fontSize: '13px', marginTop: '10px' }}>AI is crafting your questions...</p>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', maxWidth: '600px' }}>
                  <div style={{ flex: 1 }}>
                    <label className='form-label' style={{ textTransform: 'none', fontWeight: '600px', fontSize: '11px' }}>Number of questions</label>
                    <select className="form-control" value={count} onChange={(e) => setCount(e.target.value)} style={{ height: '38px', appearance: 'none', fontWeight: 'normal', WebkitAppearance: 'none', backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px', paddingRight: '30px', fontSize: '11px' }}>
                      <option value="3">3 Questions</option>
                      <option value="5">5 Questions</option>
                      <option value="10">10 Questions</option>
                      <option value="15">15 Questions</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className='form-label' style={{ textTransform: 'none', fontWeight: '600px', fontSize: '11px' }}>Difficulty level</label>
                    <select className="form-control" value={difficulty} onChange={(e) => setDifficulty(e.target.value)} style={{ height: '38px', appearance: 'none', fontWeight: 'normal', WebkitAppearance: 'none', backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px', paddingRight: '30px', fontSize: '11px' }}>
                      <option>Easy</option>
                      <option>Medium</option>
                      <option>Hard</option>
                    </select>
                  </div>


                </div>
              )}
            </div>
            <div className="lywo-modal-footer">
              <button style={{ height: '32px', fontSize: '13px' }} className="btn btn-outline-primary" onClick={onClose}>Cancel</button>
              <button className="btn-primary" style={{ height: '32px' }} onClick={generate} disabled={loading}>{loading ? 'Generating...' : 'Generate Questions'}</button>
            </div>
          </div>
        )}
        {activeTab === 'review' && (
          <div className="anim-fade" style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, overflow: 'hidden' }}>
            <div className="lywo-modal-body">
              {questions?.map((q, i) => (
                <div key={i} className="card-std" style={{ marginBottom: '0.5rem', height: 'auto', padding: '0.75rem', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <p className="text-base fw-bold" style={{ margin: 0, paddingRight: '30px', flex: 1 }}>{q?.q}</p>
                    <button className="btn-icon" onClick={() => remove(i)} title="Delete Question" style={{ border: 'none', color: '#dc3545', padding: '2px', flexShrink: 0, marginTop: '2px' }}><Icons.Trash style={{ width: '14px' }} /></button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {q?.options?.map((o, idx) => {
                      const isCorrect = o === q.correct_answer;
                      return (
                        <div key={idx} className="text-base" style={{ padding: '4px 8px', borderRadius: '4px', border: `1px solid ${isCorrect ? 'var(--color-success-text)' : '#eee'}`, background: isCorrect ? 'var(--color-success-bg)' : '#fff', display: 'flex', alignItems: 'center', gap: '6px', color: isCorrect ? 'var(--color-success-text)' : '#4b5563', fontWeight: isCorrect ? '600' : 'normal' }}>
                          {isCorrect ? <Icons.Check style={{ color: 'var(--color-success-text)', fontSize: '10px' }} /> : <span style={{ width: '10px' }}></span>}
                          <span style={{ fontWeight: '600', minWidth: '15px' }}>{String.fromCharCode(65 + idx)}.</span>
                          <span>{o}</span>
                        </div>
                      );
                    })}
                  </div>
                  {q.rationale && (<div className="information-card mt-2 text-base" style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '4px 8px' }}><strong className="fw-bold" style={{ marginRight: '5px' }}>Rationale:</strong><span>{q.rationale}</span></div>)}
                </div>
              ))}
            </div>
            <div className="lywo-modal-footer">
              <button style={{ height: '32px', fontSize: '13px' }} className="btn btn-outline-primary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" style={{ height: '32px', padding: '0 24px' }} onClick={handleImport}>Import Questions</button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};


const AiGenerator = ({ onImport, initialTopic, buttonLabel = "Generate with AI", buttonStyle }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const handleImportWrapper = (data) => { 
    onImport(data); 
    setIsOpen(false); 
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = LYWO_CSS;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <>
      <button 
        className="btn btn-primary" 
        onClick={() => setIsOpen(true)}
        style={buttonStyle} // Added buttonStyle prop here in case you need it
      >
        {/* Icon removed from here */}
        {buttonLabel}
      </button>
      
      {isOpen && (
        <AiWizardModal 
          onClose={() => setIsOpen(false)} 
          onImport={handleImportWrapper} 
          initialTopic={initialTopic} 
        />
      )}
    </>
  );
};


/******************************************************************************
 * [PREVIEW MODE ONLY] - DEMO DASHBOARD WRAPPER
 * ----------------------------------------------------------------------------
 * The 'App' component below is a demonstration wrapper to show how
 * the AiGenerator behaves and handles state when placed inside a page.
 * * LIVE INTEGRATION STEPS:
 * 1. You likely will NOT need this component in your project.
 * 2. In your existing Quiz Editor, import `AiGenerator` directly:
 * `import AiGenerator from './AiGenerator';`
 * 3. Render it inside your app UI and pass an `onImport` function prop.
 * `<AiGenerator onImport={(questions) => { ... }} />`
 ******************************************************************************/


/**
 * MODULE 4: FEATURE VIEWS
 * -----------------------------------------------------------------------------
 * This module consumes the AiGenerator and displays the managed quiz content.
 */

export default AiGenerator

